// Meta Pixel, loaded as late as possible without losing a single event.
//
// fbevents.js is ~670 KB of parse + execute, so it is NOT injected on mount.
// It waits for whichever comes first:
//   - the visitor's first real interaction (scroll / touchstart / click /
//     mousemove / keydown), or
//   - requestIdleCallback after the window `load` event.
//
// Events fired before the script exists (PageView, AddToCart) are queued here
// and flushed in order the moment the pixel initializes, so tracking is intact.

const PIXEL_ID = '1724342565264992'

let injected = false
let pending: string[] = []

function flush() {
  const fbq = (window as any).fbq
  if (!fbq) return
  for (const event of pending) fbq('track', event)
  pending = []
}

function injectPixel() {
  if (injected) return
  injected = true

  const w = window as any

  if (!w.fbq) {
    const n: any = (w.fbq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments)
    })
    if (!w._fbq) w._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)

    w.fbq('init', PIXEL_ID)
  }

  flush()
}

/** Tracks a conversion event, queueing it if the pixel is not up yet. */
export function trackPixel(event: string): void {
  pending.push(event)

  // A click IS an interaction, so this also triggers the deferred load.
  injectPixel()
}

/**
 * Queues the PageView and arms the deferred loaders.
 * Returns a cleanup function that detaches every listener.
 */
export function setupDeferredPixel(): () => void {
  pending.push('PageView')

  const interactions = [
    'scroll',
    'touchstart',
    'click',
    'mousemove',
    'keydown',
  ] as const

  let idleId: number | undefined
  let timeoutId: number | undefined

  const fire = () => {
    cleanup()
    injectPixel()
  }

  const onLoad = () => {
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined
    if (ric) {
      idleId = ric(fire, { timeout: 4000 })
    } else {
      timeoutId = window.setTimeout(fire, 2000)
    }
  }

  function cleanup() {
    for (const type of interactions) {
      window.removeEventListener(type, fire)
    }
    window.removeEventListener('load', onLoad)
    const cic = (window as any).cancelIdleCallback as
      | ((id: number) => void)
      | undefined
    if (idleId !== undefined && cic) cic(idleId)
    if (timeoutId !== undefined) window.clearTimeout(timeoutId)
  }

  for (const type of interactions) {
    window.addEventListener(type, fire, { passive: true, once: true })
  }

  if (document.readyState === 'complete') {
    onLoad()
  } else {
    window.addEventListener('load', onLoad, { once: true })
  }

  return cleanup
}
